import React from 'react'
import {
  MONTH_NAMES,
  type OrgFilterMode,
} from '../../../utils/projectOverviewLists'

export interface ProjectOverviewFiltersProps {
  orgCategoryFilter: string
  onCategoryChange: (v: string) => void
  categoryOptionValues: string[]
  orgFilterMode: OrgFilterMode
  onOrgFilterModeChange: (m: OrgFilterMode) => void
  orgFY: string
  onOrgFYChange: (v: string) => void
  orgQ: string
  onOrgQChange: (v: string) => void
  orgYear: string
  onOrgYearChange: (v: string) => void
  orgMonth: string
  onOrgMonthChange: (v: string) => void
  orgFYOptions: { value: string; label: string }[]
  orgYearOptions: number[]
}

/** Matches `TimePeriodFilters` toolbar + `AccountOwnerPortfolio` period toggle. */
const sel =
  'text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500'

export function ProjectOverviewFilters({
  orgCategoryFilter,
  onCategoryChange,
  categoryOptionValues,
  orgFilterMode,
  onOrgFilterModeChange,
  orgFY,
  onOrgFYChange,
  orgQ,
  onOrgQChange,
  orgYear,
  onOrgYearChange,
  orgMonth,
  onOrgMonthChange,
  orgFYOptions,
  orgYearOptions,
}: ProjectOverviewFiltersProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 w-full min-w-0">
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Category
        </span>
        <select
          value={orgCategoryFilter}
          onChange={(e) => onCategoryChange(e.target.value)}
          className={`${sel} min-w-[5.5rem]`}
        >
          {categoryOptionValues.map((c) => (
            <option key={c || 'all'} value={c}>
              {c ? c.charAt(0).toUpperCase() + c.slice(1) : 'All'}
            </option>
          ))}
        </select>
      </div>

      <div className="h-4 w-px bg-slate-200 shrink-0" />

      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Period
        </span>
        <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {(
            [
              ['fy', 'Indian FY'],
              ['calendar', 'Calendar'],
            ] as const
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => onOrgFilterModeChange(mode)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                orgFilterMode === mode
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {orgFilterMode === 'fy' ? (
        <>
          <div className="h-4 w-px bg-slate-200 shrink-0" />
          <select
            value={orgFY}
            onChange={(e) => onOrgFYChange(e.target.value)}
            className={`${sel} min-w-[7rem]`}
          >
            {orgFYOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={orgQ}
            onChange={(e) => onOrgQChange(e.target.value)}
            className={`${sel} min-w-[4.5rem]`}
          >
            <option value="all">All Q</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </select>
        </>
      ) : (
        <>
          <div className="h-4 w-px bg-slate-200 shrink-0" />
          <select
            value={orgYear}
            onChange={(e) => onOrgYearChange(e.target.value)}
            className={`${sel} min-w-[5.5rem]`}
          >
            <option value="all">All years</option>
            {orgYearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
          <select
            value={orgMonth}
            onChange={(e) => onOrgMonthChange(e.target.value)}
            className={`${sel} min-w-[7rem]`}
          >
            <option value="all">All months</option>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={String(i + 1)}>
                {name}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  )
}
